import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Award, TrendingUp, TrendingDown, Minus, Pencil, Lock, Unlock, X, Upload, Plus, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

const GRADE_COLOR = {
  A: "#16a34a",
  B: "#65a30d",
  C: "#ca8a04",
  D: "#ea580c",
  E: "#dc2626",
  F: "#7f1d1d",
};
const fmtTerm = (t) => (t || "").replace(/(VT|HT)(\d{4})/, "$1 $2").replace("20", "");

const GRADE_NUM = { A: 6, B: 5, C: 4, D: 3, E: 2, F: 1 };
const NUM_GRADE = { 6: "A", 5: "B", 4: "C", 3: "D", 2: "E", 1: "F" };

// Kronologisk sortering: VT/HT-prefix → halvår. VT2025 → 2025.0, HT2025 → 2025.5.
function termSortKey(t) {
  const m = /^(VT|HT)(\d{4})$/.exec(t || "");
  if (!m) return 9999;
  return parseInt(m[2], 10) + (m[1] === "HT" ? 0.5 : 0);
}

export default function GradesPage() {
  const { user } = useAuth();
  const isAdult = user && !user.is_child;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChildIdx, setActiveChildIdx] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // draft = { subjects: [{code,name,grades:{term:grade}}], official: {term: number|null} }
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/vklass/grades");
      setData(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte hämta betyg");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ALL HOOKS måste komma före early returns (React rules of hooks).
  // useMemo för att stabilisera referenser så att nedanstående useMemo-hooks
  // inte re-räknas varje render.
  const serverTerms = useMemo(() => data?.terms || [], [data]);
  const child = data?.children?.[activeChildIdx] || data?.children?.[0] || null;
  const subjects = useMemo(
    () => (editMode && draft ? draft.subjects : (child?.subjects || [])),
    [editMode, draft, child]
  );
  const officialMap = editMode && draft ? draft.official : (child?.meritvarde_official || {});
  const computedMap = child?.meritvarde_computed || {};

  // Effective terms: serverdata + manuellt tillagda (i edit-mode) — sorterade kronologiskt
  const terms = useMemo(() => {
    const extra = (editMode && draft?.extraTerms) || [];
    const all = Array.from(new Set([...serverTerms, ...extra]));
    return all.sort((a, b) => termSortKey(a) - termSortKey(b));
  }, [serverTerms, editMode, draft]);

  // (liveMerit borttagen — endast Vklass officiella värde visas)

  // chartData: en post per ÄMNE med ett fält per termin (numeriskt betygsvärde)
  const chartData = useMemo(() => {
    return subjects.map((s) => {
      const row = { name: s.name };
      for (const t of terms) {
        const g = (s.grades || {})[t];
        row[t] = g ? GRADE_NUM[g] : null;
        row[`${t}_label`] = g ? `${fmtTerm(t)} ${g}` : "";
        row[`${t}_grade`] = g || "";
      }
      return row;
    });
  }, [terms, subjects]);

  // Distinkta färger per termin — palett för 18+ terminer (4.5+ år).
  // VT-terminer i vår/sommar-toner, HT-terminer i höst/vinter-toner — kronologiskt naturligt.
  const TERM_COLORS = [
    "#97bc62", // VT — grön
    "#3d5a80", // HT — mörkblå
    "#82a0bc", // VT — ljusblå
    "#e07a5f", // HT — terrakotta
    "#65a30d", // VT — limegrön
    "#7c3aed", // HT — lila
    "#0891b2", // VT — turkos
    "#ea580c", // HT — orange
    "#16a34a", // VT — smaragdgrön
    "#dc2626", // HT — röd
    "#0284c7", // VT — sky
    "#a16207", // HT — bronze
    "#22c55e", // VT — grön2
    "#6d28d9", // HT — violett
    "#14b8a6", // VT — teal
    "#c2410c", // HT — rost
    "#84cc16", // VT — lime2
    "#9333ea", // HT — purple2
  ];

  // trendData: jämför varje konsekutiv termpar — höjda / sänkta / oförändrade
  const trendData = useMemo(() => {
    const out = [];
    for (let i = 1; i < terms.length; i++) {
      const prevT = terms[i - 1];
      const newT = terms[i];
      let up = 0, down = 0, same = 0;
      const upList = [], downList = [];
      for (const s of subjects) {
        const a = (s.grades || {})[prevT];
        const b = (s.grades || {})[newT];
        if (!a || !b) continue;
        const an = GRADE_NUM[a];
        const bn = GRADE_NUM[b];
        if (an == null || bn == null) continue;
        if (bn > an) { up += 1; upList.push(`${s.name} ${a}→${b}`); }
        else if (bn < an) { down += 1; downList.push(`${s.name} ${a}→${b}`); }
        else same += 1;
      }
      if (up + down + same > 0) {
        out.push({ from: prevT, to: newT, up, down, same, upList, downList });
      }
    }
    return out;
  }, [terms, subjects]);

  const uploadPdf = async (file) => {
    if (!child) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: r } = await api.post(
        `/vklass/grades/upload-pdf?child_user_id=${encodeURIComponent(child.user_id)}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const added = r.grades_added || 0;
      const skipped = (r.grades_skipped_existing || 0) + (r.grades_skipped_locked || 0);
      const merits = r.meritvarde_added || 0;
      toast.success(
        `PDF importerad — ${added} nya betyg, ${merits} meritvärde${
          skipped ? `. Hoppade över ${skipped} (befintliga/låsta).` : "."
        }`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF-import misslyckades");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="px-5 pt-3 pb-12" data-testid="grades-page">
        <p className="text-sm text-[color:var(--text-tertiary)]">Hämtar betyg…</p>
      </div>
    );
  }
  if (!data || !data.children || data.children.length === 0) {
    return (
      <div className="px-5 pt-3 pb-12" data-testid="grades-page">
        <div className="mb-5">
          <p className="label-eyebrow">Skolan</p>
          <h1 className="font-display text-2xl font-medium mt-0.5">Betyg & meritvärde</h1>
        </div>
        <div className="card-soft p-6 text-center">
          <Award size={28} className="mx-auto text-[color:var(--text-tertiary)] mb-2" />
          <p className="text-sm">Inga betyg hämtade ännu.</p>
          {isAdult && (
            <label className="mt-3 inline-block px-3 py-2 rounded-full bg-[color:var(--jarvis)] text-white text-xs font-medium cursor-pointer">
              <Upload size={12} className="inline mr-1 -mt-0.5" />
              {uploading ? "Importerar…" : "Ladda upp PDF"}
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadPdf(f);
                  e.target.value = "";
                }}
                data-testid="grades-upload-pdf-input-empty"
              />
            </label>
          )}
        </div>
      </div>
    );
  }

  // Senaste officiella merit (post-loading)
  const officialTerms = terms.filter((t) => officialMap[t] != null);
  const latestOfficialTerm = officialTerms[officialTerms.length - 1] || null;
  const officialValue = latestOfficialTerm ? officialMap[latestOfficialTerm] : null;

  // Låsta terminer (för denna barnvy) — kan ändras live i edit-mode
  const lockedTerms = new Set(child?.locked_terms || []);

  const toggleLock = async (term) => {
    const isLocked = lockedTerms.has(term);
    try {
      await api.put("/vklass/term-locks", {
        child_user_id: child.user_id,
        term,
        locked: !isLocked,
      });
      toast.success(isLocked ? `${fmtTerm(term)} olåst` : `${fmtTerm(term)} låst — Vklass-sync rör den inte`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte ändra lås");
    }
  };

  const enterEdit = () => {
    setDraft({
      subjects: JSON.parse(JSON.stringify(child.subjects || [])),
      official: JSON.parse(JSON.stringify(child.meritvarde_official || {})),
      extraTerms: [], // nyligen tillagda terminer (ej i serverdata)
    });
    setEditMode(true);
  };
  const cancelEdit = () => {
    setDraft(null);
    setEditMode(false);
  };

  const addTerm = () => {
    const input = window.prompt("Termin (t.ex. VT26 eller HT26):", "");
    if (!input) return;
    const m = /^\s*(VT|HT)\s*(\d{2,4})\s*$/i.exec(input);
    if (!m) {
      toast.error("Ogiltigt format. Använd VT26, HT26, VT2026 osv.");
      return;
    }
    const pref = m[1].toUpperCase();
    let yr = m[2];
    if (yr.length === 2) yr = "20" + yr;
    const term = `${pref}${yr}`;
    const existing = new Set([...terms, ...(draft?.extraTerms || [])]);
    if (existing.has(term)) {
      toast.info(`${fmtTerm(term)} finns redan`);
      return;
    }
    setDraft((d) => ({ ...d, extraTerms: [...(d.extraTerms || []), term] }));
    toast.success(`Lade till ${fmtTerm(term)} — fyll i betyg och spara`);
  };

  const addSubject = () => {
    const code = window.prompt("Ämneskod (2-3 versaler, t.ex. MA, ENG, SAM):", "");
    if (!code) return;
    const trimmedCode = code.trim().toUpperCase();
    if (!/^[A-ZÅÄÖ]{2,3}$/.test(trimmedCode)) {
      toast.error("Ämneskod måste vara 2-3 versaler");
      return;
    }
    const name = window.prompt(`Ämnesnamn för ${trimmedCode}:`, "");
    if (!name || !name.trim()) return;
    const trimmedName = name.trim();
    const duplicate = (draft.subjects || []).some(
      (s) => s.code === trimmedCode && s.name === trimmedName,
    );
    if (duplicate) {
      toast.info(`${trimmedName} finns redan`);
      return;
    }
    setDraft((d) => ({
      ...d,
      subjects: [...d.subjects, { code: trimmedCode, name: trimmedName, grades: {} }],
    }));
    toast.success(`Lade till ${trimmedName} — fyll i betyg och spara`);
  };

  const removeSubject = (code, name) => {
    if (!window.confirm(`Ta bort ${name}? Befintliga betyg i denna rad raderas vid Spara.`)) return;
    setDraft((d) => ({
      ...d,
      subjects: d.subjects.filter((s) => !(s.code === code && s.name === name)),
    }));
  };

  const setGrade = (code, name, term, val) => {
    setDraft((d) => {
      const subs = d.subjects.map((s) => {
        if (s.code !== code || s.name !== name) return s;
        const g = { ...(s.grades || {}) };
        const inh = { ...(s.inherited_by_term || {}) };
        if (!val) {
          delete g[term];
          delete inh[term];
        } else {
          g[term] = val;
        }
        return { ...s, grades: g, inherited_by_term: inh };
      });
      return { ...d, subjects: subs };
    });
  };

  const toggleInherited = (code, name, term) => {
    setDraft((d) => {
      const subs = d.subjects.map((s) => {
        if (s.code !== code || s.name !== name) return s;
        const inh = { ...(s.inherited_by_term || {}) };
        if (inh[term]) delete inh[term];
        else inh[term] = true;
        return { ...s, inherited_by_term: inh };
      });
      return { ...d, subjects: subs };
    });
  };

  const setOfficial = (term, val) => {
    setDraft((d) => {
      const off = { ...d.official };
      if (val === "" || val == null) off[term] = null;
      else off[term] = parseFloat(val);
      return { ...d, official: off };
    });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Bygg diff: original vs draft
      const origSubs = child.subjects || [];
      const origOff = child.meritvarde_official || {};
      const gradeChanges = [];
      const seen = new Set();
      // Diffa nya/ändrade
      for (const s of draft.subjects) {
        const orig = origSubs.find((o) => o.code === s.code && o.name === s.name);
        for (const t of terms) {
          const ng = (s.grades || {})[t] || null;
          const og = orig ? ((orig.grades || {})[t] || null) : null;
          const newInh = !!(s.inherited_by_term || {})[t];
          const oldInh = orig ? !!(orig.inherited_by_term || {})[t] : false;
          if (ng !== og || (ng && newInh !== oldInh)) {
            gradeChanges.push({
              subject_code: s.code,
              subject_name: s.name,
              term: t,
              grade: ng,
              ...(ng ? { inherited: newInh } : {}),
            });
          }
          seen.add(`${s.code}|${s.name}|${t}`);
        }
      }
      // Hantera helt borttagna ämnen — raden finns i orig men inte i draft
      for (const o of origSubs) {
        const stillThere = draft.subjects.some(
          (s) => s.code === o.code && s.name === o.name,
        );
        if (stillThere) continue;
        // Skicka null för varje cell som tidigare hade ett betyg → backend raderar
        for (const [t, g] of Object.entries(o.grades || {})) {
          if (g) {
            gradeChanges.push({
              subject_code: o.code,
              subject_name: o.name,
              term: t,
              grade: null,
            });
          }
        }
      }

      const meritChanges = [];
      for (const t of terms) {
        const nv = draft.official[t] ?? null;
        const ov = origOff[t] ?? null;
        if ((nv ?? null) !== (ov ?? null)) {
          meritChanges.push({ term: t, value: nv });
        }
      }

      if (!gradeChanges.length && !meritChanges.length) {
        toast.info("Inget ändrades");
        setEditMode(false);
        setDraft(null);
        setSaving(false);
        return;
      }
      await api.put("/vklass/grades/bulk", {
        child_user_id: child.user_id,
        grades: gradeChanges,
        meritvarde_official: meritChanges,
      });
      toast.success(`Sparat — ${gradeChanges.length} betyg, ${meritChanges.length} meritvärden`);
      setEditMode(false);
      setDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  // Data till linjediagrammet beräknades redan ovan (före early-returns)

  return (
    <div className="px-5 pt-3 pb-12" data-testid="grades-page">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="label-eyebrow">Skolan</p>
          <h1 className="font-display text-2xl font-medium mt-0.5 flex items-center gap-2">
            <Award size={22} className="text-amber-500" /> Betyg & meritvärde
          </h1>
        </div>
        <div className="flex flex-col gap-2">
          {isAdult && !editMode && (
            <>
              <label className="px-3 py-2 rounded-full bg-[color:var(--jarvis)] text-white text-xs font-medium inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-60">
                <Upload size={12} />
                {uploading ? "Importerar…" : "Ladda upp PDF"}
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadPdf(f);
                    e.target.value = "";
                  }}
                  data-testid="grades-upload-pdf-input"
                />
              </label>
              <button
                onClick={enterEdit}
                className="px-3 py-2 rounded-full border border-[color:var(--border-subtle)] text-xs font-medium inline-flex items-center gap-1.5"
                data-testid="grades-edit-btn"
              >
                <Pencil size={12} /> Redigera
              </button>
              <Link
                to={`/betyg/skriv-ut?child=${encodeURIComponent(child?.user_id || "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-full border border-[color:var(--border-subtle)] text-xs font-medium inline-flex items-center gap-1.5"
                data-testid="grades-print-link"
              >
                <Printer size={12} /> Skriv ut
              </Link>
            </>
          )}
          {isAdult && editMode && (
            <>
              <button
                onClick={addTerm}
                className="px-3 py-2 rounded-full border border-[color:var(--jarvis)] text-[color:var(--jarvis)] text-xs font-medium inline-flex items-center gap-1.5"
                data-testid="grades-add-term-btn"
              >
                <Plus size={12} /> Termin
              </button>
              <button
                onClick={addSubject}
                className="px-3 py-2 rounded-full border border-[color:var(--jarvis)] text-[color:var(--jarvis)] text-xs font-medium inline-flex items-center gap-1.5"
                data-testid="grades-add-subject-btn"
              >
                <Plus size={12} /> Ämne
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-3 py-2 rounded-full bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="grades-save-btn"
              >
                <Lock size={12} /> {saving ? "Sparar…" : "Lås & spara"}
              </button>
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="px-3 py-2 rounded-full border border-[color:var(--border-subtle)] text-xs font-medium inline-flex items-center gap-1.5"
                data-testid="grades-cancel-btn"
              >
                <X size={12} /> Avbryt
              </button>
            </>
          )}
        </div>
      </div>

      {/* Barn-filter */}
      {data.children.length > 1 && !editMode && (
        <div className="flex gap-2 mb-4 flex-wrap">
          {data.children.map((c, i) => (
            <button
              key={c.user_id}
              onClick={() => setActiveChildIdx(i)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                i === activeChildIdx
                  ? "bg-[color:var(--jarvis)] text-white border-[color:var(--jarvis)]"
                  : "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)] border-[color:var(--border-subtle)]"
              }`}
              data-testid={`grades-filter-${c.name.split(" ")[0].toLowerCase()}`}
            >
              {c.name.split(" ")[0]}
            </button>
          ))}
        </div>
      )}

      {/* Meritvärde-kort — endast Vklass officiella värde (inget beräknat) */}
      <div className="card-soft p-4 mb-4" data-testid="meritvarde-card">
        <div className="flex items-baseline justify-between mb-1">
          <p className="text-xs uppercase tracking-wider text-[color:var(--text-tertiary)]">
            Meritvärde {officialValue != null && `· ${fmtTerm(latestOfficialTerm)}`}
          </p>
          <TrendingUp size={14} className="text-[color:var(--jarvis)]" />
        </div>
        {officialValue != null ? (
          <>
            <p className="font-display text-4xl font-medium">
              {officialValue.toFixed(1)}
              <span className="text-base text-[color:var(--text-tertiary)] ml-1">/ 340</span>
            </p>
            <p className="text-xs text-[color:var(--text-secondary)] mt-1">
              Vklass officiella slutbetyg från {fmtTerm(latestOfficialTerm)}.
            </p>
          </>
        ) : (
          <>
            <p className="font-display text-2xl font-medium text-[color:var(--text-secondary)]">
              Inget officiellt meritvärde
            </p>
            <p className="text-xs text-[color:var(--text-secondary)] mt-1">
              {child.name.split(" ")[0]} har inget slutbetyg från Vklass än.
              {isAdult && " Du kan fylla i ett manuellt via Redigera nedan."}
            </p>
          </>
        )}
      </div>

      {/* Betygs-tabell (med edit-mode) */}
      <div className="card-soft p-3 mb-4" data-testid="grade-table">
        <p className="text-xs uppercase tracking-wider text-[color:var(--text-tertiary)] mb-3">
          Betyg över terminer {editMode && <span className="text-amber-600 ml-1">· redigerar</span>}
        </p>
        <div className="grid grid-cols-[90px_1fr] gap-x-2 text-xs">
          <div></div>
          <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${terms.length}, 1fr)` }}>
            {terms.map((t) => {
              const locked = lockedTerms.has(t);
              return (
                <div key={t} className="text-center text-[10px] text-[color:var(--text-tertiary)] flex flex-col items-center gap-0.5">
                  <span>{fmtTerm(t)}</span>
                  {editMode && isAdult && (
                    <button
                      onClick={() => toggleLock(t)}
                      className={`inline-flex items-center justify-center p-0.5 rounded ${
                        locked ? "text-amber-600" : "text-[color:var(--text-tertiary)] opacity-50 hover:opacity-100"
                      }`}
                      title={locked ? "Låst — sync rör inte. Klicka för att låsa upp." : "Olåst. Klicka för att låsa termin."}
                      data-testid={`lock-toggle-${t}`}
                    >
                      {locked ? <Lock size={10} /> : <Unlock size={10} />}
                    </button>
                  )}
                  {!editMode && locked && (
                    <Lock size={9} className="text-amber-600" />
                  )}
                </div>
              );
            })}
          </div>
          {subjects.map((s) => (
            <SubjectRow
              key={`${s.code}-${s.name}`}
              s={s}
              terms={terms}
              editMode={editMode}
              onChange={(t, v) => setGrade(s.code, s.name, t, v)}
              onToggleInherited={(t) => toggleInherited(s.code, s.name, t)}
              onRemove={() => removeSubject(s.code, s.name)}
            />
          ))}

          {/* Officiellt meritvärde-rad (redigerbar i edit-läge) */}
          <div className="font-medium text-[color:var(--text-secondary)] py-1 truncate border-t border-[color:var(--border-subtle)] mt-1 pt-2">
            Meritvärde
          </div>
          <div
            className="grid gap-1 py-1 border-t border-[color:var(--border-subtle)] mt-1 pt-2"
            style={{ gridTemplateColumns: `repeat(${terms.length}, 1fr)` }}
          >
            {terms.map((t) => {
              const v = officialMap[t];
              if (editMode) {
                return (
                  <input
                    key={t}
                    type="number"
                    step="0.5"
                    value={v ?? ""}
                    onChange={(e) => setOfficial(t, e.target.value)}
                    placeholder="–"
                    className="w-full text-center text-[10px] py-0.5 rounded border border-[color:var(--border-subtle)] bg-white"
                    data-testid={`merit-input-${t}`}
                  />
                );
              }
              return (
                <div key={t} className="text-center text-[10px] text-[color:var(--text-secondary)]">
                  {v != null ? v.toFixed(1) : "–"}
                </div>
              );
            })}
          </div>

          {/* Eget uträknat meritvärde — visas bara om någon termin saknar officiellt */}
          {Object.keys(computedMap).length > 0 && !editMode && (
            <>
              <div
                className="font-medium text-[10px] text-blue-700 py-1 truncate"
                title="Uträknat utifrån alla betyg (inkl. ärvda från tidigare terminer) för terminer som saknar officiellt Vklass-meritvärde."
                data-testid="merit-computed-label"
              >
                Eget uträknat
              </div>
              <div
                className="grid gap-1 py-1"
                style={{ gridTemplateColumns: `repeat(${terms.length}, 1fr)` }}
              >
                {terms.map((t) => {
                  const cv = computedMap[t];
                  const hasOfficial = officialMap[t] != null;
                  if (cv == null || hasOfficial) {
                    return <div key={t} className="text-center text-[10px] text-[color:var(--text-tertiary)]">–</div>;
                  }
                  return (
                    <div
                      key={t}
                      className="text-center text-[10px] font-semibold rounded text-blue-800 bg-blue-50 border border-blue-200 py-0.5"
                      data-testid={`merit-computed-${t}`}
                    >
                      {cv.toFixed(1)}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        {!editMode && (
          <p className="text-[10px] text-[color:var(--text-tertiary)] mt-3 italic">
            Klicka <Pencil size={10} className="inline -mt-0.5" /> Redigera för att ändra betyg manuellt.
          </p>
        )}
      </div>

      {/* Trend mellan terminer */}
      {trendData.length > 0 && (
        <div className="card-soft p-3 mb-4" data-testid="grade-trend">
          <p className="text-xs uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
            Trend mellan terminer
          </p>
          <ul className="space-y-2 text-xs">
            {trendData.map((t) => (
              <li key={`${t.from}-${t.to}`} className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-[color:var(--text-secondary)] min-w-[100px]">
                  {fmtTerm(t.from)} → {fmtTerm(t.to)}
                </span>
                <span
                  className="inline-flex items-center gap-1 text-emerald-700"
                  title={t.upList.join("\n") || "Inga"}
                >
                  <TrendingUp size={12} /> {t.up} höjda
                </span>
                <span
                  className="inline-flex items-center gap-1 text-red-700"
                  title={t.downList.join("\n") || "Inga"}
                >
                  <TrendingDown size={12} /> {t.down} sänkta
                </span>
                <span className="inline-flex items-center gap-1 text-[color:var(--text-secondary)]">
                  <Minus size={12} /> {t.same} oförändrade
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-[color:var(--text-tertiary)] mt-2 italic">
            Endast ämnen med betyg i båda terminerna räknas. Hovra för detaljer.
          </p>
        </div>
      )}

      {/* Stapeldiagram — Y = ämnen, X = betyg F→A, en stapel per termin per ämne */}
      <div className="card-soft p-3 mb-4" data-testid="grade-bar-chart">
        <p className="text-xs uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
          Betyg per ämne & termin
        </p>
        <p className="text-[10px] text-[color:var(--text-tertiary)] mb-3">
          Y = ämne, X = betyg (F → A). En stapel per termin med betyget markerat.
        </p>
        <div style={{ width: "100%", height: Math.max(280, 60 * subjects.length) }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              barCategoryGap={8}
              barGap={2}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
              <XAxis
                type="number"
                domain={[0, 6]}
                ticks={[1, 2, 3, 4, 5, 6]}
                tickFormatter={(v) => NUM_GRADE[v] || ""}
                tick={{ fontSize: 11, fontWeight: 600 }}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 10 }}
                width={90}
                interval={0}
              />
              <Tooltip
                contentStyle={{ fontSize: 11 }}
                formatter={(value, name, item) => {
                  const grade = item?.payload?.[`${name}_grade`];
                  return [grade || "–", fmtTerm(name)];
                }}
              />
              {terms.map((t, idx) => (
                <Bar
                  key={t}
                  dataKey={t}
                  fill={TERM_COLORS[idx % TERM_COLORS.length]}
                  radius={[0, 3, 3, 0]}
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey={`${t}_label`}
                    position="insideRight"
                    style={{ fill: "white", fontSize: 9, fontWeight: 600 }}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Custom legend — kronologisk ordning enligt backend sort */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center text-[10px] text-[color:var(--text-secondary)] mt-2 pt-2 border-t border-[color:var(--border-subtle)]">
          {terms.map((t, idx) => (
            <span key={t} className="inline-flex items-center gap-1">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: TERM_COLORS[idx % TERM_COLORS.length] }}
              />
              {fmtTerm(t)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function SubjectRow({ s, terms, editMode, onChange, onToggleInherited, onRemove }) {
  const inheritedMap = s.inherited_by_term || {};
  return (
    <>
      <div
        className="font-medium text-[color:var(--text-secondary)] py-1 truncate flex items-center gap-1"
        title={s.name}
      >
        {editMode && onRemove && (
          <button
            onClick={onRemove}
            className="shrink-0 text-red-500 hover:text-red-700 opacity-60 hover:opacity-100"
            title="Ta bort ämne"
            data-testid={`remove-subject-${s.code}-${s.name.replace(/\s+/g,'-')}`}
          >
            <X size={10} />
          </button>
        )}
        <span className="truncate">{s.name}</span>
      </div>
      <div
        className="grid gap-1 py-1"
        style={{ gridTemplateColumns: `repeat(${terms.length}, 1fr)` }}
      >
        {terms.map((t) => {
          const g = (s.grades || {})[t];
          const inherited = !!inheritedMap[t];
          if (editMode) {
            return (
              <div key={t} className="relative">
                <select
                  value={g || ""}
                  onChange={(e) => onChange(t, e.target.value || null)}
                  className={`w-full text-center text-[11px] font-bold py-0.5 rounded border ${
                    inherited
                      ? "border-blue-300 bg-blue-50"
                      : "border-[color:var(--border-subtle)] bg-white"
                  }`}
                  style={{
                    color: g ? GRADE_COLOR[g] : "#9ca3af",
                  }}
                  data-testid={`grade-cell-${s.code}-${t}`}
                >
                  <option value="">–</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                  <option value="E">E</option>
                  <option value="F">F</option>
                </select>
                {g && (
                  <button
                    onClick={() => onToggleInherited(t)}
                    type="button"
                    className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full text-[8px] font-bold leading-none flex items-center justify-center ${
                      inherited
                        ? "bg-blue-500 text-white"
                        : "bg-white border border-[color:var(--border-subtle)] text-[color:var(--text-tertiary)]"
                    }`}
                    title={inherited ? "Ärvt betyg — klicka för att ta bort markering" : "Markera som ärvt från föregående termin"}
                    data-testid={`inherited-toggle-${s.code}-${t}`}
                  >
                    {inherited ? "✓" : "↺"}
                  </button>
                )}
              </div>
            );
          }
          if (!g) {
            return (
              <div key={t} className="text-center text-[color:var(--text-tertiary)]">–</div>
            );
          }
          if (inherited) {
            return (
              <div
                key={t}
                className="text-center font-bold rounded text-blue-800 bg-blue-100 border border-blue-300 text-[11px] py-0.5"
                title="Ärvt betyg från föregående termin"
                data-testid={`grade-cell-inherited-${s.code}-${t}`}
              >
                ({g})
              </div>
            );
          }
          return (
            <div
              key={t}
              className="text-center font-bold rounded text-white text-[11px] py-0.5"
              style={{ backgroundColor: GRADE_COLOR[g] || "#999" }}
            >
              {g}
            </div>
          );
        })}
      </div>
    </>
  );
}
